import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { SupportRequestForm } from './SupportRequestForm';

/**
 * New Support Request modal (PD-15002) — opens from the Support page so a
 * customer can create a ticket without detouring to the dashboard widget.
 * Same shell treatment as the history filter tray: bottom sheet on mobile,
 * centered dialog on md+. The form inside is the shared SupportRequestForm.
 */
export function SupportRequestModal({
	isOpen,
	onClose,
	onCreated,
}: {
	isOpen: boolean;
	onClose: () => void;
	/** Called with the new issue id after a successful submission. */
	onCreated: (issueId: number) => void;
}): JSX.Element | null {
	const { t } = useTranslation();

	// Close on Escape while open — mirrors the filter tray's behavior.
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		const onKey = (e: KeyboardEvent): void => {
			// PD-15027 — an Escape that closed the form's Category dropdown
			// arrives handled (the dropdown prevents its default); it closes
			// only that menu, and the next Escape closes the modal.
			if (e.key === 'Escape' && !e.defaultPrevented) {
				onClose();
			}
		};
		document.addEventListener('keydown', onKey);
		return (): void => {
			document.removeEventListener('keydown', onKey);
		};
	}, [isOpen, onClose]);

	if (!isOpen) {
		return null;
	}

	return (
		<>
			<button
				type="button"
				aria-label={t('Cancel')}
				onClick={onClose}
				className="fixed inset-0 z-40 bg-black/40 drawer-backdrop-in"
			/>
			<div
				className="fixed inset-x-0 bottom-[max(calc(36px_+_env(safe-area-inset-bottom)),48px)] z-40 rounded-t-2xl bg-brand-surface shadow-2xl flex flex-col overflow-hidden filter-tray-in max-h-[80vh] md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-md md:rounded-2xl md:border md:border-brand-border"
				role="dialog"
				aria-modal="true"
				aria-label={t('New Support Request')}
			>
				<div className="flex items-center justify-between px-4 h-12 border-b border-brand-border shrink-0">
					<span className="text-base font-semibold text-default">
						{t('New Support Request')}
					</span>
					<button
						type="button"
						onClick={onClose}
						aria-label={t('Close')}
						className="text-brand-muted hover:text-default min-h-touch min-w-touch flex items-center justify-center"
					>
						<FontAwesomeIcon icon={faTimes} />
					</button>
				</div>

				<div className="flex-1 overflow-y-auto px-4 py-4">
					<SupportRequestForm
						onSuccess={onCreated}
						submitLabel="Submit Request"
					/>
				</div>
			</div>
		</>
	);
}
